import { type ReactNode } from 'react';
import { Stack, Text, Title } from '@mantine/core';
import { Button, type ForgeButtonProps } from './Button';

export interface EmptyStateProps {
  /** Illustration slot — typically a Lucide icon at 40-56 px. */
  illustration?: ReactNode;
  title: string;
  description?: string;
  /** Optional CTA. Pass null to suppress. */
  cta?: {
    label: string;
    onClick: () => void;
    variant?: ForgeButtonProps['variant'];
  };
  className?: string;
  style?: React.CSSProperties;
}

/**
 * EmptyState — every list, table, and grid must use this when its data is
 * empty (PLAN.md mandates "every list has a designed empty state, not a
 * blank pane"). Provide a meaningful illustration, a short title, an action
 * if the user can do something about it.
 */
export function EmptyState({
  illustration,
  title,
  description,
  cta,
  className,
  style,
}: EmptyStateProps) {
  return (
    <Stack
      align="center"
      justify="center"
      gap="md"
      py="xl"
      px="xl"
      {...(className !== undefined ? { className } : {})}
      style={{
        borderRadius: 12,
        border: '1px dashed var(--c-bd)',
        backgroundColor: 'rgb(var(--c-deep))',
        textAlign: 'center',
        ...style,
      }}
      role="status"
      aria-live="polite"
    >
      {illustration ? (
        // Neutral muted tint — the illustration is supporting art, not an
        // accent. (Call sites used to override this with text-txt-muted, so the
        // same primitive rendered cyan on one route and grey on another.)
        <div style={{ color: 'rgb(var(--c-txt-muted))' }}>{illustration}</div>
      ) : null}
      <Title order={4} className="font-display" style={{ color: 'rgb(var(--c-txt-bright))' }}>
        {title}
      </Title>
      {description ? (
        <Text size="sm" c="dimmed" maw={420}>
          {description}
        </Text>
      ) : null}
      {cta ? (
        <Button variant={cta.variant ?? 'primary'} onClick={cta.onClick}>
          {cta.label}
        </Button>
      ) : null}
    </Stack>
  );
}
